import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import type { AiProviderNameDto } from '@/shared/api/ai-provider';
import { Loader } from '@/shared/ui/Loader';
import { Notification } from '@/shared/ui';

import {
  useAiProviderSettings,
  useUpdateAiProviderSettings,
} from '../model/useAiProviderSettings';

const DEFAULT_DEEPSEEK_MODEL = 'deepseek-chat';
const DEFAULT_DEEPSEEK_BASE_URL = 'https://api.deepseek.com';
const DEFAULT_OPENAI_MODEL = 'gpt-4.1-mini';
const DEFAULT_ANTHROPIC_MODEL = 'claude-3-5-haiku-latest';
const DEFAULT_GEMINI_MODEL = 'gemini-2.5-flash';

function getDefaultModel(provider: AiProviderNameDto) {
  if (provider === 'openai') return DEFAULT_OPENAI_MODEL;
  if (provider === 'anthropic') return DEFAULT_ANTHROPIC_MODEL;
  if (provider === 'gemini') return DEFAULT_GEMINI_MODEL;
  if (provider === 'deepseek') return DEFAULT_DEEPSEEK_MODEL;
  return 'mock-v1';
}

function getProviderModel(provider: AiProviderNameDto, value: string | null | undefined) {
  const trimmed = value?.trim();
  if (!trimmed || trimmed === 'mock-v1') return getDefaultModel(provider);
  return trimmed;
}

function getApiKeyHint(value: string | null | undefined) {
  return value?.replace(/^\.+/, '') || '';
}

function canSaveProviderSettings(input: {
  provider: AiProviderNameDto;
  model: string;
  baseUrl: string;
  apiKey: string;
  hasSavedKey: boolean;
  disabled: boolean;
}) {
  if (input.disabled) return false;
  if (input.provider === 'mock') return true;
  if (input.model.trim() === '') return false;
  if (input.provider === 'deepseek' && input.baseUrl.trim() === '') return false;
  if (!input.hasSavedKey && input.apiKey.trim() === '') return false;
  return true;
}

export function AiProviderConfigPanel({
  accentColor,
  disabled,
}: {
  accentColor: string;
  disabled: boolean;
}) {
  const { t } = useTranslation('company');
  const { data, isPending, isError, error } = useAiProviderSettings();
  const updateMutation = useUpdateAiProviderSettings();
  const [provider, setProvider] = useState<AiProviderNameDto>('mock');
  const [model, setModel] = useState(DEFAULT_DEEPSEEK_MODEL);
  const [baseUrl, setBaseUrl] = useState(DEFAULT_DEEPSEEK_BASE_URL);
  const [apiKey, setApiKey] = useState('');
  const [isApiKeyEditing, setIsApiKeyEditing] = useState(false);

  useEffect(() => {
    if (!data) return;
    const initialProvider = !data.configured && data.provider === 'mock'
      ? 'deepseek'
      : data.provider;
    setProvider(initialProvider);
    setModel(getProviderModel(initialProvider, data.model));
    setBaseUrl(
      initialProvider === 'deepseek'
        ? data.base_url || DEFAULT_DEEPSEEK_BASE_URL
        : ''
    );
    setApiKey('');
    setIsApiKeyEditing(false);
  }, [data]);

  const hasSavedKeyForSelectedProvider = data?.configured && data.provider === provider;
  const savedApiKeyMask =
    provider !== 'mock' && hasSavedKeyForSelectedProvider
      ? getApiKeyHint(data.api_key_hint)
      : '';
  const apiKeyInputValue =
    savedApiKeyMask && !isApiKeyEditing && apiKey === ''
      ? savedApiKeyMask
      : apiKey;
  const showApiKeyRequired =
    provider !== 'mock' && !hasSavedKeyForSelectedProvider && apiKey.trim() === '';

  const saveDisabled = useMemo(
    () =>
      !canSaveProviderSettings({
        provider,
        model,
        baseUrl,
        apiKey,
        hasSavedKey: Boolean(hasSavedKeyForSelectedProvider),
        disabled: disabled || updateMutation.isPending,
      }),
    [
      provider,
      model,
      baseUrl,
      apiKey,
      hasSavedKeyForSelectedProvider,
      disabled,
      updateMutation.isPending,
    ]
  );

  if (isPending) return <Loader containerSize="small" />;

  return (
    <Stack
      spacing={1.5}
      sx={{
        mt: 0.5,
        p: 1.5,
        border: '1px solid',
        borderColor: (theme) => alpha(theme.palette.text.primary, 0.1),
        borderRadius: 1,
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.025),
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
        justifyContent="space-between"
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            fontWeight={800}
            sx={{ color: 'text.primary' }}
          >
            {t('modules.aiProvider.title')}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary' }}
          >
            {t('modules.aiProvider.caption')}
          </Typography>
        </Box>
        <Chip
          size="small"
          color={data?.configured ? 'success' : 'default'}
          label={
            data?.configured
              ? t('modules.aiProvider.status.connected')
              : t('modules.aiProvider.status.notConnected')
          }
          sx={{
            flexShrink: 0,
            borderRadius: 1,
            bgcolor: data?.configured ? alpha(accentColor, 0.1) : 'action.hover',
            color: data?.configured ? 'success.dark' : 'text.secondary',
            border: '1px solid',
            borderColor: data?.configured
              ? alpha(accentColor, 0.22)
              : (theme) => alpha(theme.palette.text.primary, 0.12),
          }}
        />
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'minmax(140px, 180px) minmax(0, 1fr)',
            lg: 'minmax(140px, 180px) minmax(0, 1fr) minmax(0, 1fr)',
          },
          gap: 1.5,
        }}
      >
        <TextField
          select
          size="small"
          label={t('modules.aiProvider.fields.provider')}
          value={provider}
          disabled={disabled || updateMutation.isPending}
          sx={{
            '& .MuiInputBase-root': {
              borderRadius: 1,
            },
          }}
          onChange={(event) => {
            const nextProvider = event.target.value as AiProviderNameDto;
            setProvider(nextProvider);
            setModel(getDefaultModel(nextProvider));
            setBaseUrl(nextProvider === 'deepseek' ? DEFAULT_DEEPSEEK_BASE_URL : '');
            setApiKey('');
            setIsApiKeyEditing(false);
          }}
        >
          <MenuItem value="mock">
            {t('modules.aiProvider.providers.mock')}
          </MenuItem>
          <MenuItem value="openai">
            {t('modules.aiProvider.providers.openai')}
          </MenuItem>
          <MenuItem value="anthropic">
            {t('modules.aiProvider.providers.anthropic')}
          </MenuItem>
          <MenuItem value="deepseek">
            {t('modules.aiProvider.providers.deepseek')}
          </MenuItem>
          <MenuItem value="gemini">
            {t('modules.aiProvider.providers.gemini')}
          </MenuItem>
        </TextField>

        <TextField
          size="small"
          label={t('modules.aiProvider.fields.model')}
          value={model}
          disabled={disabled || provider === 'mock' || updateMutation.isPending}
          onChange={(event) => setModel(event.target.value)}
          sx={{
            '& .MuiInputBase-root': {
              borderRadius: 1,
            },
          }}
        />

        <TextField
          size="small"
          label={t('modules.aiProvider.fields.baseUrl')}
          value={baseUrl}
          disabled={disabled || provider !== 'deepseek' || updateMutation.isPending}
          onChange={(event) => setBaseUrl(event.target.value)}
          sx={{
            gridColumn: { xs: 'auto', sm: '1 / -1', lg: 'auto' },
            '& .MuiInputBase-root': {
              borderRadius: 1,
            },
          }}
        />

        <TextField
          size="small"
          type="password"
          label={t('modules.aiProvider.fields.apiKey')}
          value={apiKeyInputValue}
          disabled={disabled || provider === 'mock' || updateMutation.isPending}
          onFocus={() => setIsApiKeyEditing(true)}
          onBlur={() => {
            if (apiKey === '') setIsApiKeyEditing(false);
          }}
          onChange={(event) => setApiKey(event.target.value)}
          helperText={
            provider !== 'mock' && hasSavedKeyForSelectedProvider
              ? t('modules.aiProvider.apiKeyKeepHint')
              : undefined
          }
          sx={{
            gridColumn: { xs: 'auto', sm: '1 / -1', lg: '2 / -1' },
            '& .MuiInputBase-root': {
              borderRadius: 1,
            },
            '& .MuiFormHelperText-root': {
              color: 'text.secondary',
            },
          }}
        />
      </Box>

      {showApiKeyRequired && (
        <Typography variant="body2" color="warning.main">
          {t('modules.aiProvider.apiKeyRequired')}
        </Typography>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          disabled={saveDisabled}
          onClick={() => {
            updateMutation.mutate({
              provider,
              model: provider !== 'mock' ? model.trim() : undefined,
              base_url: provider === 'deepseek' ? baseUrl.trim() : undefined,
              api_key: apiKey.trim() || undefined,
            }, {
              onSuccess: () => {
                setApiKey('');
                setIsApiKeyEditing(false);
              },
            });
          }}
          sx={{
            minWidth: { xs: '100%', sm: 220 },
            minHeight: 38,
            borderRadius: 1,
            textTransform: 'none',
            fontWeight: 800,
          }}
        >
          {t('modules.aiProvider.save')}
        </Button>
      </Box>

      <Notification
        open={isError || updateMutation.isError}
        title={error?.message || updateMutation.error?.message}
      />
    </Stack>
  );
}
